import React, { useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from "../utils/apiAssetUrl";
import '../CSS/Global.css';
import jsQR from 'jsqr';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Spinner, Text } from '@chakra-ui/react';
import Header from './Header';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const apiBase = getApiBaseUrl();

const ScanQR = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const stopScanRef = useRef(false);

  const login = JSON.parse(localStorage.getItem("login")) || ""
  const [formData, setFormData] = useState({
    permanentId: '',
    amount: '',
    vendor_permanent_Id: login.permanent_ID,
  });
  const [isScanned, setIsScanned] = useState(false);
  const navigate = useNavigate();
  const [recentData, setRecentData] = useState([])
  const [errorMessage, setErrorMessage] = useState("");
  const [load, setLoad] = useState(true);
  const [cameraLoading, setCameraLoading] = useState(true);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");

  useEffect(() => {
    if (isScanned) {
      return undefined;
    }

    stopScanRef.current = false;
    setCameraLoading(true);
    setCameraReady(false);
    setCameraError("");

    let stream = null;

    const stopAll = () => {
      stopScanRef.current = true;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      const v = videoRef.current;
      if (v) {
        v.srcObject = null;
      }
    };

    const tick = () => {
      if (stopScanRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (video.readyState >= video.HAVE_ENOUGH_DATA) {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w && h) {
          if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
          }
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(video, 0, 0, w, h);
          try {
            const imageData = ctx.getImageData(0, 0, w, h);
            const code = jsQR(imageData.data, w, h);
            if (code && code.data) {
              stopScanRef.current = true;
              setFormData((prev) => ({ ...prev, permanentId: code.data }));
              setIsScanned(true);
              return;
            }
          } catch {
            /* frame read can fail briefly while video resizes */
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const openCamera = async () => {
      try {
        const tryConstraints = [
          {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: false,
          },
          {
            video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } },
            audio: false,
          },
          { video: true, audio: false },
        ];

        let lastErr;
        for (const constraints of tryConstraints) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
            break;
          } catch (e) {
            lastErr = e;
          }
        }
        if (!stream) throw lastErr || new Error('Camera unavailable');

        streamRef.current = stream;
        const video = videoRef.current;
        if (!video || stopScanRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;

        await video.play().catch(() => {});

        const onRunning = () => {
          if (stopScanRef.current) return;
          setCameraReady(true);
          setCameraLoading(false);
          rafRef.current = requestAnimationFrame(tick);
        };

        if (video.readyState >= video.HAVE_ENOUGH_DATA) {
          onRunning();
        } else {
          video.addEventListener('loadeddata', onRunning, { once: true });
          video.addEventListener('canplay', onRunning, { once: true });
        }
      } catch (err) {
        console.error('Error accessing the camera: ', err);
        setCameraError('Allow camera access to scan, or enter the ID below manually.');
        setCameraLoading(false);
        setCameraReady(false);
      }
    };

    openCamera();

    return () => {
      stopAll();
    };
  }, [isScanned]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const submitformData = async (e) => {
    e.preventDefault();
    setLoad(false)
    try {
      const response = await fetch(`${apiBase}/vendorSell`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          vendor_permanent_Id: formData.vendor_permanent_Id,
          user_permanent_Id: formData.permanentId,
          amount: formData.amount
        }),
      })
      const res = await response.json();
      if (response.ok) {
        toast.success('Added Successfully!', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        navigate("/vendor-wallet");
        setLoad(true)
      } else if (response.status === 400) {
        setErrorMessage(res.msg || "Something went wrong.");
        setLoad(true)

      }
    } catch (error) {
      console.log(error);
      setErrorMessage(error.msg || "Something went wrong.");
      setLoad(true)
    }
  }

  const fetchRecentData = () => {
    fetch(`${apiBase}/vendorSell/${login.permanent_ID}`)
      .then((res) => res.json())
      .then((data) => {
        const sortedData = data.sort((a, b) => new Date(b.operationDate) - new Date(a.operationDate));
        const uniqueUsers = new Map();
        sortedData.forEach((transaction) => {
          if (!uniqueUsers.has(transaction.user_permanent_Id)) {
            uniqueUsers.set(transaction.user_permanent_Id, transaction);
          }
        });
        const filteredData = Array.from(uniqueUsers.values());
        setRecentData(filteredData);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchRecentData();
  }, [])

  const walletPath = login && login.type === 'vendor' ? '/vendor-wallet' : '/wallet';

  return (
    <>
      <Header />
      <ToastContainer />
      <Box mt={"40px"} display={{ base: "none", sm: "none", md: "none", lg: "block", xl: "block" }}>
        <Text fontSize={"3xl"} textAlign={"center"} color={"red"}>Oops!</Text>
        <Text fontSize={"2xl"} textAlign={"center"}>Scan QR is not available in this device.</Text>
        <Text fontSize={"xl"} textAlign={"center"}>Try with your mobile device</Text>
      </Box>
      <div className="scan scan--page">
        <Link to={walletPath} className="scan-wallet-back-link">
          <i className="fa-solid fa-wallet" aria-hidden="true" />
          <span>Wallet</span>
        </Link>
        <p className="scan-headline">Scan customer QR</p>
        <p className="scan-sub">Point the camera at a code — it reads automatically</p>

        {!isScanned && (
          <div className="scanner-wrapper">
            <div className={`scanner-viewport ${cameraReady ? 'scanner-viewport--live' : ''}`}>
              {cameraLoading && (
                <div className="scanner-loading" aria-live="polite">
                  <Spinner thickness="3px" speed="0.65s" color="white" size="lg" />
                  <span>Starting camera…</span>
                </div>
              )}
              {cameraError && (
                <div className="scanner-error-banner" role="alert">
                  {cameraError}
                </div>
              )}
              <video
                ref={videoRef}
                className="scanner-video"
                autoPlay
                playsInline
                muted
              />
              <canvas ref={canvasRef} className="scanner-canvas-hidden" aria-hidden="true" />
              <div className="scanner-frame-overlay" aria-hidden="true">
                <span className="scanner-corner scanner-corner--tl" />
                <span className="scanner-corner scanner-corner--tr" />
                <span className="scanner-corner scanner-corner--bl" />
                <span className="scanner-corner scanner-corner--br" />
                <span className="scanner-scan-line" />
              </div>
            </div>
          </div>
        )}

        <p className="scan-align">Or enter details manually</p>

        <div className={`manual scan-manual ${isScanned ? 'full-screen' : ''}`}>
          <form onSubmit={submitformData}>
            <div className="manual-input">
              <input
                type="text"
                name="permanentId"
                placeholder="Customer permanent ID"
                value={formData.permanentId}
                onChange={handleInputChange}
                onFocus={() => setIsScanned(true)}
                required
              />
            </div>
            <div className="manual-input">
              <input
                type="number"
                name="amount"
                placeholder="Amount (₹)"
                value={formData.amount}
                onChange={handleInputChange}
                required
              />
            </div>
            {
              load ? <button type="submit" className="submit-btn">
                Submit
              </button> : <button className="submit-btn" type="button">
                <Spinner />
              </button>
            }
            {isScanned && (
              <button type="button" className="close-btn" onClick={() => setIsScanned(false)} aria-label="Close form">
                ✖
              </button>
            )}

            {errorMessage && (
              <p className="error-message scan-form-error">
                {errorMessage}
              </p>
            )}
          </form>
        </div>
        <div className="manual-recent scan-recent">
          <p className="recent">Recent</p>
          <div className="recent-people">
            {
              recentData.map((ele) => (
                <div
                  className="recent-people-list"
                  key={ele.user_permanent_Id || ele.id}
                  onClick={() => {
                    setFormData((prevData) => ({
                      ...prevData,
                      permanentId: ele.user_permanent_Id,
                    }));
                    setIsScanned(true);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setFormData((prev) => ({ ...prev, permanentId: ele.user_permanent_Id }));
                      setIsScanned(true);
                    }
                  }}
                >
                  <img src="https://picsum.photos/50/50" alt="" />
                  <p>{ele.user_name}</p>
                </div>
              ))
            }
          </div>
        </div>

      </div>
    </>
  );
};

export default ScanQR;
